<template>
    <div class="project-bubble-card">
        <!-- HEADER -->
        <div class="project-bubble-header">
            <div class="header-left mr-5px">
                <span v-if="!isSearchVisible" class="header-title">{{ $t('Projects.projects') }}</span>
                <div v-else class="bubble-header-search">
                    <img :src="searchIcon" :alt="$t('PlaceHolder.search')">
                    <input
                        ref="bubbleSearchInput"
                        v-model="search"
                        type="text"
                        :placeholder="$t('PlaceHolder.search')"
                    >
                </div>
            </div>
            <div class="header-actions">
                <button class="header-icon-btn" @click="toggleHeaderSearch">
                    <img :src="searchIcon" :alt="$t('PlaceHolder.search')">
                </button>
                <!-- <button class="header-icon-btn">
                    <img :src="moreIcon" alt="More">
                </button> -->
                <button class="header-icon-btn" @click="emit('close')">
                    <img :src="closeIcon" :alt="$t('Common.close')">
                </button>
            </div>
        </div>

        <!-- OVERVIEW -->
        <div class="project-section cursor-pointer" @click="emit('overview')">
            <div class="project-item-left">
                <div class="section-item overview">
                    <img :src="overviewIcon" class="section-icon">
                </div>
                <span class="section-label">{{ $t('ProjectSlider.overview') }}</span>
            </div>
            <span class="section-count">{{ projectCounts.overview }}</span>
        </div>

        <!-- ARCHIVE -->
        <div class="project-section">
            <div class="project-item-left">
                <div class="section-item overview" @click="emit('archive')">
                    <img :src="archiveIcon" class="section-icon">
                </div>
                <span class="section-label">{{ $t('Projects.archive') }}</span>
            </div>
            <span class="section-count">{{ projectCounts.archive }}</span>
        </div>

        <!-- HEADER LIST -->
        <div class="project-section list-projects-header">
            <div class="panel-title">{{ $t('ProjectSlider.projects') }}</div>

            <div class="list-actions">
                <ProjectFilter
                    v-if="Object.keys(projectData).length"
                    :projectData="projectData"
                    variant="bubble"
                    class="header-icon-btn"
                    @apply="applyFilter"
                    @clear="clearFilter"
                />

                <button
                    class="header-icon-btn"
                    @click="handleCreateProject"
                    v-if="checkPermission('project.project_list') && checkPermission('project.project_create')"
                >
                    <img :src="addIcon">
                </button>
            </div>
        </div>

        <!-- PROJECT LIST -->
        <div class="project-section project-list-section">
            <ProjectListComponent
                    ref="projectListRef"
                    :loadedProjects="loadedProjects"
                    :projectItems="projectList"
                    :filterFavorites="filterFavorites"
                    :projectData="projectData"
                    :loadingData="loadingData"
                    :showArchivedProjects="showArchivedProjects"
                    :ProjectfilterObject="ProjectfilterObject"
                    v-model:loader="loader"
                    :isSpinner="isSpinner"
                    :expandedId="expandedId"
                    :sprintFolders="sprintFolders"
                    :isProjectFilterApplied="isProjectFilterApplied"
                    :isSearchAPIWait="isSearchAPIWait"
                    :mutateCurrentProjectDetails="mutateCurrentProjectDetails"
                    @update:showArchivedProjects="handleShowArchivedProjectsUpdate"
                    @update:filterFavorites="handleFilterFavoritesUpdate"
                    @changeAvatar="$emit('changeAvatar', $event)"
                    @createProject="$emit('createProject', $event)"
                    @loadMoreProjects="loadMoreProjects"
                    @projectDataClick="projectDataClick()"
                    @archiveCountChange="handleArchiveCountChange"
                    @close="visible = false"
                    @applyFilter="(query, sortByField, sortByOrder) => applyFilter(query, sortByField, sortByOrder)"
                    @clearFilter="clearFilter($event)"
                    @update:search="handleSearchUpdate"
                    @updateSearchProject="handleSearchUpdateProject"
                />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineEmits, defineProps, defineExpose, provide, inject, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useCustomComposable } from '@/composable'
import { useProjectsHelper } from '@/views/Projects/helper'
import { apiRequest } from '@/services'
import * as env from '@/config/env'

import ProjectFilter from '@/components/molecules/ProjectFilter/ProjectFilter.vue'

import searchIcon from '@/assets/images/svg/search.svg'
// import moreIcon from '@/assets/images/svg/horizontalDots.svg'
import closeIcon from '@/assets/images/svg/double_arrow_left.svg'
import overviewIcon from '@/assets/images/svg/overview.svg'
import archiveIcon from '@/assets/images/svg/archive.svg'
import addIcon from '@/assets/images/svg/add_circle_outline.svg'
import ProjectListComponent from '@/components/molecules/ProjectListComponent/ProjectListComponent.vue'

const emit = defineEmits(['close', 'overview', 'archive', 'createProject', 'update:projectData', 'update:showArchivedProjects', 'update:filterFavorites', 'changeAvatar', 'loadMoreProjects', 'projectDataClick'])
const props = defineProps({
    projectData: {
        type: Object,
        default: () => ({})
    },
    showArchivedProjects: {
        type: Boolean,
        default: false
    },
    filterFavorites: {
        type: Boolean,
        default: false
    },
    loadingProjects: {
        type: Boolean,
        default: false
    }
})

const loader = ref(false)
const isSpinner = ref(false)
const expandedId = ref('')
const sprintFolders = ref({})
const loadedSprintFolderProjects = new Set()
const loadingSprintFolderProjects = new Set()
const isSearchAPIWait = ref(false)
const loadingData = ref({})
const projectListRef = ref(null)

const { checkPermission, debounce } = useCustomComposable()
const { projects, filterdProjects, filteredProjects, projectListFilters, dispatchProjects } = useProjectsHelper()
const { dispatch, commit, getters } = useStore()
const route = useRoute()
const router = useRouter()
const userId = inject('$userId')
const companyId = inject('$companyId')
const companyUserDetail = computed(() => getters['settings/companyUserDetail'])

/* ================= STATE ================= */
const search = ref('')
const isSearchVisible = ref(false)
const bubbleSearchInput = ref(null)
const projectSearch = ref(true)
const folderSearch = ref(false)
const sprintSearch = ref(false)
const filterFavorites = ref(props.filterFavorites)
const showArchivedProjects = ref(props.showArchivedProjects)
provide('showArchivedProjects', showArchivedProjects)

const isLoadingProjects = ref(false)
const isArchiveCountLoaded = ref(false)
const archiveTotalCount = ref(0)

const visible = ref(true)
const ProjectfilterObject = ref({
    query: {},
    sortByField: {},
    sortByOrder: {}
})

const isProjectFilterApplied = ref(false)
/* ✅ selected project */
const projectData = ref(props.projectData && Object.keys(props.projectData).length ? props.projectData : {})
provide('selectedProject', projectData)

/* ================= LOAD MORE ================= */
const itemsPerPage = ref(30);
const loadedProjects = ref([]);
const projectList = computed(() => {
    let list = []
    if (Array.isArray(filterdProjects.value)) {
        list = filterdProjects.value
    } else if (filterdProjects.value?.list) {
        list = filterdProjects.value.list
    }

    return sortPinnedProjects(list)
})
watch(projectList, () => {
    loadedProjects.value = []
    loadMoreProjects()
}, { deep: true })
watch(() => props.projectData, (val) => {
    if (val?._id && val._id !== projectData.value?._id) {
        projectData.value = val
    }
}, { deep: true })
watch(() => props.showArchivedProjects, (val) => {
    if (val !== showArchivedProjects.value) {
        showArchivedProjects.value = val
    }
})
watch(() => props.filterFavorites, (val) => {
    if (val !== filterFavorites.value) {
        filterFavorites.value = val
    }
})
const loadMoreProjects = () => {
  const start = loadedProjects.value.length;
  const end = start + itemsPerPage.value;

  if (start < projectList.value.length) {
    let newProjects = projectList.value.slice(start, end);
    const finalProject = newProjects.map((x)=>{
        return {
            projectIcon: x.projectIcon,
            ProjectName: x.ProjectName,
            id: x.id,
            isGlobalPermission:x.isGlobalPermission,
            deletedStatusKey:x.deletedStatusKey,
            projectStatusData:x.projectStatusData,
            status: x.status,
            isPrivateSpace: x.isPrivateSpace,
            _id: x._id,
            isRestrict:x.isRestrict,
            createdAt:x.createdAt,
            lastProjectActivity:x.lastProjectActivity,
            userId:x.userId,
            sprintsfolders:x.sprintsfolders,
            sprintsObj:x.sprintsObj,
            favouriteTasks:x.favouriteTasks,
            userActivity:x.userActivity,
            ProjectRequiredComponent: x.ProjectRequiredComponent,
            sprintData: x.sprintData
        }
    })
    loadedProjects.value = [...loadedProjects.value, ...finalProject];
  }
};

function syncSelectedProjectFromRoute() {
    const routeProjectId = route.params?.id
    const currentProjects = Array.isArray(projects.value) ? projects.value : []

    if (routeProjectId && currentProjects.length) {
        const matchedProject = currentProjects.find((item) => item._id === routeProjectId)
        if (matchedProject) {
            projectData.value = matchedProject
            expandedId.value = matchedProject._id
            commit('projectData/mutateCurrentProjectDetails', matchedProject)
            return
        }
    }

    if (props.projectData?._id) {
        projectData.value = props.projectData
        expandedId.value = props.projectData._id
        return
    }

    if (!projectData.value?._id && currentProjects.length) {
        projectData.value = currentProjects[0]
        expandedId.value = currentProjects[0]._id
        commit('projectData/mutateCurrentProjectDetails', currentProjects[0])
    }
}


function mutateCurrentProjectDetails(data, updateRoute = false, isClicked = false) {
    if (!data) return

    const shouldExpand = !(expandedId.value === data._id && isClicked)
    if (expandedId.value === data._id && isClicked) {
        expandedId.value = ''
    } else {
        expandedId.value = data._id
    }

    let tab = route.query?.tab
    if (!data?.ProjectRequiredComponent?.find((x) => x.keyName === route.query?.tab)) {
        const viewFind = data?.ProjectRequiredComponent?.find((e) => e.setAsDefault) || data?.ProjectRequiredComponent?.find((e) => e.viewStatus) || data?.ProjectRequiredComponent?.[0]
        tab = viewFind ? viewFind.keyName : 'ProjectListView'
    }

    if (updateRoute && companyId?.value) {
        router.push({
            name: 'Project',
            params: {
                cid: companyId.value,
                id: data._id
            },
            query: {
                ...route.query,
                tab
            }
        })
    }

    const currentProject = projects.value.find((item) => item._id === data._id) || data
    projectData.value = currentProject
    commit('projectData/mutateCurrentProjectDetails', currentProject)
    emit('update:projectData', currentProject)

    // 🔥 THÊM DÒNG NÀY
    if (shouldExpand) {
        loadProjectSprintFolders(data._id)
    }
}
/* ================= FILTER ================= */

async function runFilter(isSearchUpdate = false) {
    try {
        isLoadingProjects.value = true
        loader.value = true

        if (showArchivedProjects.value) {
            await filteredProjects(JSON.parse(JSON.stringify(projects.value || [])), true, filterFavorites.value)
        } else if (shouldUseLocalProjectNameSearch()) {
            filterdProjects.value = filterProjectsByName()
        } else {
            await projectListFilters({
                projectSearch,
                folderSearch,
                sprintSearch,
                filterFavorites: filterFavorites.value,
                search,
                showArchivedProjects: showArchivedProjects.value,
                isSearchUpdate,
                isProjectFilterApplied: isProjectFilterApplied.value,
                ProjectfilterObject: ProjectfilterObject.value
            })
        }

    } catch (e) {
        console.error(e)
    } finally {
        isLoadingProjects.value = false
        if (!isSearchAPIWait.value) {
            loader.value = false
        }
    }
}

function shouldUseLocalProjectNameSearch() {
    return Boolean(
        search.value?.trim() &&
        projectSearch.value &&
        !folderSearch.value &&
        !sprintSearch.value &&
        !isProjectFilterApplied.value &&
        !Object.keys(ProjectfilterObject.value.query || {}).length &&
        !Object.keys(ProjectfilterObject.value.sortByField || {}).length &&
        !Object.keys(ProjectfilterObject.value.sortByOrder || {}).length
    )
}

function filterProjectsByName() {
    const keyword = search.value.trim().toLowerCase()
    const source = Array.isArray(projects.value) ? projects.value : []
    const filtered = source.filter((project) => {
        const projectName = project?.ProjectName || ""
        return project.status !== "close" && !project.deletedStatusKey && projectName.toLowerCase().includes(keyword)
    })

    return sortProjectList(filterFavorites.value ? sortFavoriteProjects(filtered) : filtered)
}

function sortFavoriteProjects(projectList = []) {
    const favourites = projectList.filter((project) => project.favouriteTasks?.some((fav) => fav.userId === userId.value))
    const others = projectList.filter((project) => !project.favouriteTasks?.some((fav) => fav.userId === userId.value))

    return [...sortProjectList(favourites), ...sortProjectList(others)]
}

function sortProjectList(projectList = []) {
    return [...projectList].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
}

function hasProjectSort(sortByField = {}, sortByOrder = {}) {
    return Boolean(Object.keys(sortByField || {}).length && Object.keys(sortByOrder || {}).length)
}

function hasProjectQuery(query = {}) {
    return Boolean(Object.keys(query || {}).length)
}

function isSortOnlyFilter(query = {}, sortByField = {}, sortByOrder = {}) {
    return !hasProjectQuery(query) && hasProjectSort(sortByField, sortByOrder) && !search.value
}

function getComparableProjectValue(project = {}, field = '') {
    switch (field) {
        case 'name':
            return String(project.ProjectName || '').toLowerCase()
        case 'created_at':
            return new Date(project.createdAt || 0).getTime()
        case 'due_date':
            return new Date(project.DueDate || project.dueDate || 0).getTime()
        case 'comments_count':
            return Number(project.comments_count || project.commentsCount || project.commentCounts || 0)
        case 'running_time_tracker':
            return Number(project.running_time_tracker || project.runningTimeTracker || project.runningTimeTrackerCount || 0)
        default:
            return new Date(project.createdAt || 0).getTime()
    }
}

function compareByProjectSort(a, b) {
    const sortByField = ProjectfilterObject.value.sortByField || {}
    const sortByOrder = ProjectfilterObject.value.sortByOrder || {}

    if (!hasProjectSort(sortByField, sortByOrder)) {
        return new Date(b.createdAt) - new Date(a.createdAt)
    }

    const direction = sortByOrder.value === 'asc' ? 1 : -1
    const aValue = getComparableProjectValue(a, sortByField.value)
    const bValue = getComparableProjectValue(b, sortByField.value)

    if (aValue === bValue) {
        return new Date(b.createdAt) - new Date(a.createdAt)
    }

    return aValue > bValue ? direction : -direction
}

function isProjectPinned(project = {}) {
    return Boolean(project.favouriteTasks?.some((fav) => fav.userId === userId.value))
}

function sortPinnedProjects(projectList = []) {
    return [...projectList].sort((a, b) => {
        const pinDiff = Number(isProjectPinned(b)) - Number(isProjectPinned(a))
        if (pinDiff !== 0) return pinDiff
        return compareByProjectSort(a, b)
    })
}

function getLocalProjectListForSort() {
    const source = showArchivedProjects.value ? normalizeProjectList(filterdProjects.value) : normalizeProjectList(projects.value)

    return source.filter((project) => {
        if (showArchivedProjects.value) {
            return true
        }
        return project.status !== 'close' && !project.deletedStatusKey
    })
}

/* ================= LOAD ================= */
onMounted(async () => {
    await dispatchProjects()   // 🔥 QUAN TRỌNG
    await loadArchiveTotalCount()
    await runFilter(false)     // 🔥 QUAN TRỌNG
    loadMoreProjects()         // 🔥 THÊM LOAD MORE
})

/* ================= WATCH ================= */
syncSelectedProjectFromRoute()
watch(
    () => [
        search.value,
        projectSearch.value,
        folderSearch.value,
        sprintSearch.value,
        filterFavorites.value,
        showArchivedProjects.value,
        isProjectFilterApplied.value
    ],
    debounce(() => runFilter(true), 300),
    { deep: true }
)

watch(filterdProjects, (newVal, oldVal) => {
    if(JSON.stringify(newVal) !== JSON.stringify(oldVal)){
        loadedProjects.value = [];
        loadMoreProjects();
    }
}, { deep: true })

/* ================= FILTER ACTION ================= */
function applyFilter(query, sortByField, sortByOrder) {
    isSearchAPIWait.value = true
    loader.value = true
    isProjectFilterApplied.value = false
    ProjectfilterObject.value = { query, sortByField, sortByOrder }

    if (isSortOnlyFilter(query, sortByField, sortByOrder)) {
        filterdProjects.value = getLocalProjectListForSort()
        loadedProjects.value = []
        loadMoreProjects()
        isSearchAPIWait.value = false
        loader.value = false
        return
    }

    const filterPayload = {
        type: 'projectFilter',
        query,
        sortByField,
        sortByOrder,
        fields: '_id',
        showArchived: showArchivedProjects.value
    }

    if (search.value) {
        if (projectSearch.value) {
            filterPayload.type = 'projectFilter_projectName'
        } else if (folderSearch.value) {
            filterPayload.type = 'projectFilter_folder'
        } else if (sprintSearch.value) {
            filterPayload.type = 'projectFilter_sprint'
        }
        filterPayload.search = search.value
    }

    apiRequest('post', `${env.PROJECTSEARCH}`, filterPayload)
        .then((searchedData) => {
            commit('projectData/mutateSearchedProjects', { data: searchedData?.data || [], searchType: 'projectFilter' })
            isProjectFilterApplied.value = true
            return projectListFilters({
                projectSearch,
                folderSearch,
                sprintSearch,
                filterFavorites: filterFavorites.value,
                search,
                showArchivedProjects: showArchivedProjects.value,
                isSearchUpdate: false,
                isProjectFilterApplied: true,
                ProjectfilterObject: ProjectfilterObject.value
            })
        })
        .then(() => {
            loadedProjects.value = []
            loadMoreProjects()
        })
        .catch((error) => {
            console.error(error)
        })
        .finally(() => {
            isSearchAPIWait.value = false
            loader.value = false
        })
}

function clearFilter() {
    const hasFilter = Boolean(
        Object.keys(ProjectfilterObject.value.query || {}).length ||
        Object.keys(ProjectfilterObject.value.sortByField || {}).length ||
        Object.keys(ProjectfilterObject.value.sortByOrder || {}).length
    )

    if (!isProjectFilterApplied.value && !hasFilter) {
        return
    }

    loader.value = true
    isProjectFilterApplied.value = false
    ProjectfilterObject.value = { query: {}, sortByField: {}, sortByOrder: {} }
    runFilter(Boolean(search.value))
}

/* ================= SELECT PROJECT ================= */
watch(filterdProjects, (val) => {
    if (projectData.value?._id || !val?.[0]) {
        return
    }
    projectData.value = val[0] || {
        isGlobalPermission: null
    }
    if (projectData.value?._id) {
        emit('update:projectData', projectData.value)
    }
})

/* ================= COUNTS ================= */
const projectCounts = computed(() => {
    const projectSource = normalizeProjectList(projects.value)

    return {
        overview: projectSource.filter((project) => !isArchivedProject(project)).length,
        archive: archiveTotalCount.value
    }
})

/* ================= ACTION ================= */
function normalizeProjectList(projectList = []) {
    if (Array.isArray(projectList)) {
        return projectList
    }
    if (Array.isArray(projectList?.data)) {
        return projectList.data
    }
    return Array.isArray(projectList?.list) ? projectList.list : []
}

function isArchivedProject(project = {}) {
    return project.deletedStatusKey === 2 || project.status === 'close' || project.archived
}

function addArchivedItem(set, type, item = {}) {
    if (item?.deletedStatusKey !== 2) {
        return
    }

    const id = item._id || item.id || item.folderId
    if (id) {
        set.add(`${type}:${id}`)
    }
}

function getArchivedSprintAndCategoryCountFromProjects(projectList = []) {
    const archivedItems = new Set()

    projectList.forEach((project) => {
        Object.values(project?.sprintsObj || {}).forEach((sprint) => {
            addArchivedItem(archivedItems, 'sprint', sprint)
        })

        Object.values(project?.sprintsfolders || {}).forEach((folder) => {
            addArchivedItem(archivedItems, 'category', folder)
            Object.values(folder?.sprintsObj || {}).forEach((sprint) => {
                addArchivedItem(archivedItems, 'sprint', sprint)
            })
        })
    })

    return archivedItems.size
}

function getArchivedSprintAndCategoryCountFromResponse(responseData = {}, assignedProjectIds = new Set()) {
    const archivedItems = new Set()
    const isAssignedProject = (projectId) => assignedProjectIds.has(projectId)

    ;(responseData?.matchedDocuments || []).forEach((sprint) => {
        if (isAssignedProject(sprint.projectId)) {
            addArchivedItem(archivedItems, 'sprint', sprint)
        }

        (sprint.folderData || []).forEach((folder) => {
            if (isAssignedProject(folder.projectId)) {
                addArchivedItem(archivedItems, 'category', folder)
            }
        })
    })

    ;(responseData?.additionalFolders || []).forEach((folder) => {
        if (isAssignedProject(folder.projectId)) {
            addArchivedItem(archivedItems, 'category', folder)
        }
    })

    return archivedItems.size
}

async function loadArchiveTotalCount() {
    if (isArchiveCountLoaded.value) {
        return
    }

    const projectSource = normalizeProjectList(projects.value)
    const assignedProjectIds = new Set(projectSource.map((project) => project._id || project.id).filter(Boolean))

    if (!assignedProjectIds.size) {
        archiveTotalCount.value = 0
        isArchiveCountLoaded.value = true
        return
    }

    try {
        const response = await apiRequest('post', `${env.PROJECTSEARCH}`, {
            search: '',
            type: 'showArchiveOnly',
            showArchived: true
        })
        archiveTotalCount.value = getArchivedSprintAndCategoryCountFromResponse(response?.data?.[0] || {}, assignedProjectIds)
    } catch (error) {
        console.error('ERROR in loadArchiveTotalCount', error)
        archiveTotalCount.value = getArchivedSprintAndCategoryCountFromProjects(projectSource)
    } finally {
        isArchiveCountLoaded.value = true
    }
}

function handleArchiveCountChange(delta = 0) {
    archiveTotalCount.value = Math.max(0, archiveTotalCount.value + Number(delta || 0))
}

function handleCreateProject() {
    emit('createProject')
}

function toggleHeaderSearch() {
    isSearchVisible.value = !isSearchVisible.value
    if (isSearchVisible.value) {
        nextTick(() => {
            bubbleSearchInput.value?.focus()
        })
        return
    }
    search.value = ''
}

function handleSearchUpdate(searchTerm) {
    search.value = searchTerm;
}

function handleSearchUpdateProject(searchValues) {
    projectSearch.value = searchValues.projectSearch;
    folderSearch.value = searchValues.folderSearch;
    sprintSearch.value = searchValues.sprintSearch;
}

function handleShowArchivedProjectsUpdate(value) {
    showArchivedProjects.value = value;
    emit('update:showArchivedProjects', value);
}

function handleFilterFavoritesUpdate(value) {
    filterFavorites.value = value;
    emit('update:filterFavorites', value);
}

function projectDataClick() {
    // Có thể emit hoặc handle click
    emit('projectDataClick');
}
async function loadProjectSprintFolders(projectId) {
    if (!projectId || loadedSprintFolderProjects.has(projectId) || loadingSprintFolderProjects.has(projectId)) {
        return
    }

    try {
        loadingSprintFolderProjects.add(projectId)
        loadingData.value[projectId] = true
        const [sprints, folders] = await Promise.all([
            dispatch('projectData/setSprints', { projectId }),
            dispatch('projectData/setFolders', { projectId })
        ])

        const sprintsArray = (sprints || [])
            .filter((sprint) => sprint.projectId === projectId && !sprint.folderId)
            .map((sprint) => ({ ...sprint, id: sprint._id }))

        const foldersObject = (folders || []).reduce((acc, folder) => {
            if (folder.projectId === projectId) {
                acc[folder._id] = {
                    ...folder,
                    id: folder._id,
                    folderId: folder._id,
                    sprintsObj: {}
                }
            }
            return acc
        }, {})

        ;(sprints || []).forEach((sprint) => {
            if (sprint.projectId === projectId && sprint.folderId && foldersObject[sprint.folderId]) {
                foldersObject[sprint.folderId].sprintsObj[sprint._id] = {
                    ...sprint,
                    id: sprint._id,
                    folderName: foldersObject[sprint.folderId].name
                }
            }
        })

        sprintFolders.value = {
            ...sprintFolders.value,
            [projectId]: {
                folders: foldersObject,
                sprints: sprintsArray
            }
        }

        const project = projects.value.find((item) => item._id === projectId)
        if (project) {
            const sprintIdToObject = {}
            sprintsArray.forEach((sprint) => {
                sprintIdToObject[sprint.id] = sprint
            })

            project.sprintsObj = sprintIdToObject
            project.sprintsfolders = {
                ...(project?.sprintsfolders && Object.keys(project.sprintsfolders).length ? project.sprintsfolders : {}),
                ...foldersObject
            }

            commit('projectData/mutateProjects', [{
                snap: null,
                privateSnap: false,
                userId: userId?.value,
                roleType: companyUserDetail.value?.roleType,
                op: 'modified',
                data: { ...project }
            }])

            if (search.value && (projectSearch.value || isProjectFilterApplied.value)) {
                commit('projectData/mutateExistingSearchedProjects', project)
            }
        }

        nextTick(() => {
            loadingData.value[projectId] = false
        })
        loadedSprintFolderProjects.add(projectId)
    } catch (e) {
        console.error('load sprint/folder error', e)
        loadingData.value[projectId] = false
    } finally {
        loadingSprintFolderProjects.delete(projectId)
    }
}

function toggleSidebar() {
    return
}

defineExpose({ toggleSidebar })

</script>

<style src="./style.css"></style>
